import { useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import clock from '~/assets/icons/clock.svg';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, formatTime, numWord } from '~/shared';
import { PinCodeResetModal } from '../PinCodeResetModal';
import {
	StyledContainer,
	StyledErrorText,
	StyledForgotPincode,
	StyledLogo,
	StyledPincodeContainer,
	StyledPincodeDot,
	StyledPincodeDotsContainer,
	StyledTimerContainer,
} from './StartOverlay.styled';

interface IStartOverlayProps {
	onSuccess?: () => void;
}

const StartOverlay: React.FC<IStartOverlayProps> = ({ onSuccess }) => {
	const banExpiresAt = useObservable(rage.bank.getBanExpiresAt());
	const pinCodeLength = useObservable(rage.bank.getPinCodeLength());
	const isPinCodeSet = useObservable(rage.bank.getIsPinCodeSet());
	const [showPincode, setShowPincode] = useState(false);
	const [attemptsLeft, setAttemptsLeft] = useState(0);
	const [showError, setShowError] = useState(false);
	const [pincode, setPincode] = useState('');
	const [isConfirmStep, setIsConfirmStep] = useState(false);
	const [firstPin, setFirstPin] = useState('');
	const [isPinResetOpen, setIsPinResetOpen] = useState(false);
	const timerTextRef = useRef<HTMLDivElement>(null);
	const [isTimerVisible, setIsTimerVisible] = useState(() =>
		banExpiresAt ? banExpiresAt - Date.now() > 0 : false,
	);

	useInputAction(
		InputAction.CommonMenuClose,
		() => {
			rage.bank.close().catch((err) => rage.notifications.showException(err));
		},
		{ allowWhileFocused: true },
	);

	useEffectOnce(() => {
		const timeout = setTimeout(() => {
			setShowPincode(true);
		}, 1000);

		return () => clearTimeout(timeout);
	});

	const handleShowError = (resetFn: () => void) => {
		setShowError(true);
		setTimeout(() => {
			setShowError(false);
			resetFn();
		}, 3000);
	};

	useEffect(() => {
		if (pincode.length !== pinCodeLength) {
			return;
		}

		if (isPinCodeSet) {
			rage.bank
				.checkPinCode(pincode)
				.then(({ isSucceed, attemptsLeft }) => {
					if (isSucceed) {
						onSuccess?.();
					} else {
						setAttemptsLeft(attemptsLeft ?? 0);
						handleShowError(() => setPincode(''));
					}
				})
				.catch((err) => rage.notifications.showException(err));
		} else if (!isConfirmStep) {
			setFirstPin(pincode);
			setIsConfirmStep(true);
			setPincode('');
		} else {
			if (pincode === firstPin) {
				rage.bank
					.setPinCode(pincode)
					.then(() => onSuccess?.())
					.catch((err) => rage.notifications.showException(err));
			} else {
				handleShowError(() => {
					setIsConfirmStep(false);
					setFirstPin('');
					setPincode('');
				});
			}
		}
	}, [pincode]);

	useEffect(() => {
		if (!showPincode || isTimerVisible || isPinResetOpen) return;

		rage.input
			.setAnyInputFocused(true)
			.catch((err) => rage.notifications.showException(err));

		const handleGlobalKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Backspace' || e.key === 'Delete') {
				setPincode((prev) => prev.slice(0, -1));
				return;
			}

			if (!/^\d$/.test(e.key)) {
				return;
			}

			setPincode((prev) => {
				if (prev.length >= pinCodeLength) {
					return prev;
				}

				const newCode = (prev + e.key).slice(0, pinCodeLength);
				return newCode;
			});
		};

		window.addEventListener('keydown', handleGlobalKeyDown);

		return () => {
			window.removeEventListener('keydown', handleGlobalKeyDown);
			rage.input
				.setAnyInputFocused(false)
				.catch((err) => rage.notifications.showException(err));
		};
	}, [
		showPincode,
		onSuccess,
		isTimerVisible,
		isPinCodeSet,
		isConfirmStep,
		firstPin,
		pinCodeLength,
		isPinResetOpen,
	]);

	useEffect(() => {
		if (!banExpiresAt) {
			setIsTimerVisible(false);
			return;
		}

		let intervalId: NodeJS.Timer | undefined = undefined;

		const update = () => {
			const diff = banExpiresAt - Date.now();

			setIsTimerVisible(diff > 0);

			if (diff <= 0) {
				clearInterval(intervalId);
				if (timerTextRef.current) {
					timerTextRef.current.textContent = '00:00';
				}
				return;
			}

			if (!timerTextRef.current) return;

			timerTextRef.current.textContent =
				formatTime(diff, '{mm}:{ss}') || '00:00';
		};

		update();
		intervalId = setInterval(update, 1000);

		return () => {
			clearInterval(intervalId);
		};
	}, [banExpiresAt]);

	const titleText = useMemo(() => {
		if (isPinCodeSet) {
			if (isTimerVisible) {
				return 'Повторите попытку позже';
			}

			if (attemptsLeft > 0) {
				return `Остал${
					attemptsLeft === 1 ? 'а' : 'о'
				}сь ${attemptsLeft} ${numWord(attemptsLeft, [
					'попытка',
					'попытки',
					'попыток',
				])}`;
			}

			return 'Введите пин-код';
		}

		if (isConfirmStep) {
			return 'Повторите пин-код';
		}

		return 'Придумайте пин-код';
	}, [isPinCodeSet, isConfirmStep, isTimerVisible, attemptsLeft]);

	return (
		<StyledContainer>
			<StyledLogo $top={showPincode || isTimerVisible} />
			{(showError || isTimerVisible) && (
				<StyledErrorText>Неправильный пин-код</StyledErrorText>
			)}
			<StyledPincodeContainer $show={showPincode || isTimerVisible}>
				<Text>{titleText}</Text>
				<StyledPincodeDotsContainer $length={pinCodeLength}>
					{Array.from({ length: pinCodeLength }).map((_, index) => (
						<StyledPincodeDot
							key={index}
							$active={index < pincode.length}
							$error={showError || isTimerVisible}
						/>
					))}
				</StyledPincodeDotsContainer>
			</StyledPincodeContainer>
			<StyledTimerContainer $show={isTimerVisible}>
				<img src={clock} />
				<Text ref={timerTextRef}>
					{banExpiresAt
						? formatTime(Math.max(banExpiresAt - Date.now(), 0), '{mm}:{ss}')
						: '00:00'}
				</Text>
			</StyledTimerContainer>
			<StyledForgotPincode
				$show={showPincode || isTimerVisible}
				onClick={() => {
					if (!showPincode && !isTimerVisible) return;
					setIsPinResetOpen(true);
				}}
			>
				Забыли пин-код?
			</StyledForgotPincode>
			<PinCodeResetModal
				open={isPinResetOpen}
				onClose={() => setIsPinResetOpen(false)}
				onSuccess={() => {
					setPincode('');
					setIsConfirmStep(false);
					setFirstPin('');
				}}
			/>
		</StyledContainer>
	);
};

export { StartOverlay };
